// A page that holds unsaved work registers a question here, and every way out
// of it asks that question first: another page from the rail (navigationStore),
// a resource change from the breadcrumb, the rail or Recientes
// (useResourceRoute), the browser's back and forward (the selection binding),
// and a reload or a closed tab (beforeunload). One page is open at a time, so
// one question is enough.
import { useEffect } from 'react';
import i18next from 'i18next';
import { confirmDialog } from '../design-system/components/ConfirmDialog';

let question: string | null = null;

/** Run `exit` now when the page may be left, or once the user chooses to leave
 *  it. `onStay` runs at once whenever the user is asked, for an exit the
 *  browser already started (back, forward). */
export function leaveThen(exit: () => void, onStay?: () => void): void {
  if (question === null) { exit(); return; }
  onStay?.();
  void confirmDialog({
    title: i18next.t('shell.leave.title', 'Cambios sin guardar'),
    message: question,
    confirmLabel: i18next.t('shell.leave.discard', 'Salir sin guardar'),
    cancelLabel: i18next.t('shell.leave.stay', 'Seguir editando'),
    danger: true,
  }).then(ok => { if (ok) exit(); });
}

/** Register the question; the returned function removes it again. */
export function setLeaveGuard(message: string): () => void {
  question = message;
  return () => { if (question === message) question = null; };
}

/** Ask `message` before leaving while `active`; the browser asks its own
 *  question on reload or close. */
export function useLeaveGuard(active: boolean, message: string): void {
  useEffect(() => {
    if (!active) return;
    const release = setLeaveGuard(message);
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      release();
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [active, message]);
}
